/**
 * MULTI PL: resolve Per-PL Audited for a period slice (current or prior).
 * Prefer live maps from plMultiPeriodChainStore; fallback to periods[].plLine.
 */

function sumNodeAdj(node: any): number {
    const list = Array.isArray(node?.adjustment) ? node.adjustment
        : (Array.isArray(node?.adjustByPapers) ? node.adjustByPapers : []);
    return list.reduce((s: number, a: any) => s + (Number(a?.adjustment) || 0), 0);
}

/** Leaf audited = opening/client + adjustments; null if fsId not found. */
export function bootstrapFsAuditedFromPlLine(
    nodes: any[] | null | undefined,
    fsId: number,
): number | null {
    if (!fsId || !Number.isFinite(fsId)) return null;
    let found: number | null = null;
    const visit = (list: any[]) => {
        for (const n of list || []) {
            const children = Array.isArray(n?.children) ? n.children : [];
            if (children.length > 0) {
                visit(children);
                continue;
            }
            if (Number(n?.fsId) !== fsId) continue;
            const client = Number(n?.openingAmount ?? n?.client ?? n?.amount ?? 0) || 0;
            found = client + sumNodeAdj(n);
        }
    };
    visit(nodes || []);
    return found;
}

export function lookupPlSliceAudited(
    auditedBySlice: Record<string, Record<number, number>> | null | undefined,
    periods: Array<{key: string; yearOfAssessment?: string | null; plLine?: any[]}> | null | undefined,
    sliceKey: string | null | undefined,
    fsId: number,
    /** Top-level annual PL tree fallback when slice map / period.plLine missing */
    annualPlLine?: any[] | null,
): number | null {
    const key = String(sliceKey || '').trim();
    if (!key || !fsId) return null;

    const map = auditedBySlice?.[key];
    if (map && Object.prototype.hasOwnProperty.call(map, fsId)) {
        return Number(map[fsId]) || 0;
    }

    const period = (periods || []).find((p) => p.key === key);
    if (period?.plLine && period.plLine.length > 0) {
        const fromPeriod = bootstrapFsAuditedFromPlLine(period.plLine, fsId);
        if (fromPeriod != null) return fromPeriod;
    }

    // Fallback: annual tree leaf opening for this slice YOA (no live adj chain yet)
    if (annualPlLine?.length && period?.yearOfAssessment) {
        const yoa = String(period.yearOfAssessment).trim();
        const fromAnnual = bootstrapFsAuditedFromPlLineForYoa(annualPlLine, fsId, yoa);
        if (fromAnnual != null) return fromAnnual;
    }
    return null;
}

/** Like bootstrapFsAuditedFromPlLine but prefer amountsByYearOfAssessment[yoa] for opening. */
function bootstrapFsAuditedFromPlLineForYoa(
    nodes: any[] | null | undefined,
    fsId: number,
    yoa: string,
): number | null {
    if (!fsId || !Number.isFinite(fsId) || !yoa) return null;
    let found: number | null = null;
    const visit = (list: any[]) => {
        for (const n of list || []) {
            const children = Array.isArray(n?.children) ? n.children : [];
            if (children.length > 0) {
                visit(children);
                continue;
            }
            if (Number(n?.fsId) !== fsId) continue;
            const byYoa = n?.amountsByYearOfAssessment as Record<string, number | string> | null | undefined;
            let client = Number(n?.openingAmount ?? n?.client ?? n?.amount ?? 0) || 0;
            if (byYoa && typeof byYoa === 'object' && Object.keys(byYoa).length > 0) {
                const raw = byYoa[yoa];
                client = (raw !== undefined && raw !== null && raw !== '') ? (Number(raw) || 0) : 0;
            }
            // Do not add annual Σ adjustments here — they are not YOA-scoped.
            found = client;
        }
    };
    visit(nodes || []);
    return found;
}
